import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { format, isValid, parse, startOfDay, subDays } from "date-fns";
import type { FilterState } from "@/hooks/useFilters";
import type { FilterChip as GenericFilterChip } from "@/components/shared/filters/AppliedFilterChips";

export type DashboardFilterKey =
  | "period"
  | "materials"
  | "states"
  | "establishments"
  | "generators";

export interface DashboardFilters {
  dateRange: { start: Date; end: Date };
  /** ids de material */
  materials: string[];
  /** siglas de UF */
  states: string[];
  /** external_id de estabelecimento */
  establishments: string[];
  /** external_id de gerador */
  generators: string[];
}

/** Rótulo de cada filtro, como aparece no dropdown e no chip. */
export const FILTER_LABELS: Record<DashboardFilterKey, string> = {
  period: "Período",
  materials: "Tipo de resíduo",
  states: "Estado (UF)",
  establishments: "Estabelecimento",
  generators: "Gerador",
};

const PARAM = {
  start: "de",
  end: "ate",
  materials: "materiais",
  states: "ufs",
  establishments: "unidades",
  generators: "geradores",
} as const;

const URL_DATE_FORMAT = "yyyy-MM-dd";
const CHIP_DATE_FORMAT = "dd/MM/yy";
/** Acima disso, os chips de um filtro de lista viram um único chip-resumo. */
const CHIP_COLLAPSE_THRESHOLD = 3;

/** Últimos 30 dias, incluindo hoje. */
export const getDefaultDateRange = (today: Date = new Date()) => ({
  start: startOfDay(subDays(today, 29)),
  end: startOfDay(today),
});

const parseDate = (value: string | null): Date | null => {
  if (!value) return null;
  const parsed = parse(value, URL_DATE_FORMAT, new Date());
  return isValid(parsed) ? startOfDay(parsed) : null;
};

const parseList = (value: string | null): string[] =>
  value
    ? value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)
    : [];

const isSameDay = (a: Date, b: Date) =>
  format(a, URL_DATE_FORMAT) === format(b, URL_DATE_FORMAT);

export const isDefaultDateRange = (
  dateRange: DashboardFilters["dateRange"],
  today: Date = new Date(),
): boolean => {
  const fallback = getDefaultDateRange(today);
  return (
    isSameDay(dateRange.start, fallback.start) &&
    isSameDay(dateRange.end, fallback.end)
  );
};

/**
 * Lê os filtros da querystring. Param ausente, malformado ou intervalo
 * incompleto cai no default — link antigo não quebra a tela.
 */
export const parseDashboardFilters = (
  params: URLSearchParams,
  today: Date = new Date(),
): DashboardFilters => {
  const start = parseDate(params.get(PARAM.start));
  const end = parseDate(params.get(PARAM.end));
  const hasValidRange = start !== null && end !== null && start <= end;

  return {
    dateRange: hasValidRange ? { start, end } : getDefaultDateRange(today),
    materials: parseList(params.get(PARAM.materials)),
    states: parseList(params.get(PARAM.states)),
    establishments: parseList(params.get(PARAM.establishments)),
    generators: parseList(params.get(PARAM.generators)),
  };
};

/** Serializa só o que difere do default, para `/dashboard` ficar limpo sem filtro. */
export const serializeDashboardFilters = (
  filters: DashboardFilters,
  today: Date = new Date(),
): URLSearchParams => {
  const params = new URLSearchParams();

  if (!isDefaultDateRange(filters.dateRange, today)) {
    params.set(PARAM.start, format(filters.dateRange.start, URL_DATE_FORMAT));
    params.set(PARAM.end, format(filters.dateRange.end, URL_DATE_FORMAT));
  }
  if (filters.materials.length) {
    params.set(PARAM.materials, filters.materials.join(","));
  }
  if (filters.states.length) params.set(PARAM.states, filters.states.join(","));
  if (filters.establishments.length) {
    params.set(PARAM.establishments, filters.establishments.join(","));
  }
  if (filters.generators.length) {
    params.set(PARAM.generators, filters.generators.join(","));
  }

  return params;
};

export type FilterChip = GenericFilterChip<DashboardFilterKey>;

/** Mapa de id → nome para exibição, montado a partir de useFilterOptions. */
export type FilterOptionLabels = Partial<
  Record<Exclude<DashboardFilterKey, "period">, Map<string, string>>
>;

export const buildFilterChips = (
  filters: DashboardFilters,
  labels: FilterOptionLabels = {},
  today: Date = new Date(),
): FilterChip[] => {
  const chips: FilterChip[] = [];

  if (!isDefaultDateRange(filters.dateRange, today)) {
    chips.push({
      key: "period",
      label: FILTER_LABELS.period,
      display: `${format(filters.dateRange.start, CHIP_DATE_FORMAT)} - ${format(
        filters.dateRange.end,
        CHIP_DATE_FORMAT,
      )}`,
    });
  }

  const listKeys: Exclude<DashboardFilterKey, "period">[] = [
    "materials",
    "states",
    "establishments",
    "generators",
  ];

  for (const key of listKeys) {
    const values = filters[key];

    // Acima do limite, um chip por valor inunda a tela (ex.: "Selecionar
    // todos" em milhares de estabelecimentos) — vira um único chip-resumo.
    // Mesmo limite já usado pelos badges do filtro de Estabelecimento
    // (EstablishmentMultiSelect: `selectedOptions.length > 3`).
    if (values.length > CHIP_COLLAPSE_THRESHOLD) {
      chips.push({
        key,
        label: FILTER_LABELS[key],
        display: `${values.length} selecionados`,
      });
      continue;
    }

    for (const value of values) {
      chips.push({
        key,
        value,
        label: FILTER_LABELS[key],
        display: labels[key]?.get(value) ?? value,
      });
    }
  }

  return chips;
};

export const countActiveFilters = (
  filters: DashboardFilters,
  today: Date = new Date(),
): number => buildFilterChips(filters, {}, today).length;

/**
 * Remove um valor de um filtro de lista, ou devolve o período ao default.
 * Sem `value` (chip-resumo "N selecionados"), limpa o filtro inteiro — não há
 * um valor pontual pra remover.
 */
export const removeFilterValue = (
  filters: DashboardFilters,
  key: DashboardFilterKey,
  value?: string,
  today: Date = new Date(),
): DashboardFilters => {
  if (key === "period") {
    return { ...filters, dateRange: getDefaultDateRange(today) };
  }
  if (value === undefined) {
    return { ...filters, [key]: [] };
  }
  return {
    ...filters,
    [key]: filters[key].filter((item) => item !== value),
  };
};

/**
 * Descarta estabelecimentos que não pertencem mais aos geradores
 * selecionados — a cascata Gerador → Estabelecimento que a barra de
 * filtros atual já tem.
 */
export const pruneEstablishments = (
  filters: DashboardFilters,
  availableEstablishmentIds: string[],
): DashboardFilters => {
  const available = new Set(availableEstablishmentIds);
  const establishments = filters.establishments.filter((id) =>
    available.has(id),
  );
  return establishments.length === filters.establishments.length
    ? filters
    : { ...filters, establishments };
};

/**
 * Adapta para o FilterState que os hooks de dados da dashboard atual esperam.
 * TEMPORÁRIO: morre junto com useFilters quando os widgets novos entrarem.
 */
export const toLegacyFilterState = (
  filters: DashboardFilters,
): FilterState => ({
  dateRange: filters.dateRange,
  dateField: null,
  materials: filters.materials,
  states: filters.states,
  establishments: filters.establishments,
  generators: filters.generators,
  statuses: [],
  packagings: [],
  residues: [],
  hasMtr: null,
});

interface UseDashboardFiltersResult {
  filters: DashboardFilters;
  /** Aplica um subconjunto dos filtros, preservando o resto. */
  setFilters: (next: Partial<DashboardFilters>) => void;
  removeFilter: (key: DashboardFilterKey, value?: string) => void;
  clearAll: () => void;
  activeCount: number;
}

/**
 * Filtros das páginas de dashboard, com o estado na querystring — o link
 * filtrado é compartilhável e sobrevive a reload. O default (últimos 30 dias)
 * não é escrito na URL.
 */
export const useDashboardFilters = (): UseDashboardFiltersResult => {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(
    () => parseDashboardFilters(searchParams),
    [searchParams],
  );

  const apply = useCallback(
    (next: DashboardFilters) => {
      // replace: filtrar não empilha histórico — voltar sai da página.
      setSearchParams(serializeDashboardFilters(next), { replace: true });
    },
    [setSearchParams],
  );

  const setFilters = useCallback(
    (next: Partial<DashboardFilters>) => apply({ ...filters, ...next }),
    [apply, filters],
  );

  const removeFilter = useCallback(
    (key: DashboardFilterKey, value?: string) =>
      apply(removeFilterValue(filters, key, value)),
    [apply, filters],
  );

  const clearAll = useCallback(
    () =>
      apply({
        dateRange: getDefaultDateRange(),
        materials: [],
        states: [],
        establishments: [],
        generators: [],
      }),
    [apply],
  );

  const activeCount = useMemo(() => countActiveFilters(filters), [filters]);

  return { filters, setFilters, removeFilter, clearAll, activeCount };
};
